import { max, min, differenceInDays, compareDesc } from 'date-fns'

export type ResourceForSorting = {
  startAt: Date
  endAt: Date
  updatedAt: Date
}

/**
 * Sorts resources with start and end dates by relevancy to a given date range using a percentage-based scoring system.
 *
 * How it works:
 * 1. Calculates date overlap between the provided date range (e.g. from a moment) and each resources's date range
 * 2. Computes a relevancy score as: overlap_days / plan_duration_days
 * 3. This prioritizes shorter, more focused plans (e.g. sub-plans) over longer plans when overlap is equal
 *    Example: A 7-day plan with 1 day overlap (14.3% relevancy) ranks higher than
 *             a 365-day plan with 1 day overlap (0.3% relevancy)
 * 4. For resources with identical relevancy scores, sorts by most recently updated
 * 5. Resources with no overlap get 0% relevancy and fall back to update date sorting
 *
 * @param resources - Array of resources to sort
 * @param startDate - Start date for overlap calculation (required)
 * @param endDate - End date for overlap calculation (optional, defaults to startDate for single-day events)
 * @returns Sorted array of resources (most relevant first)
 */
export function sortResourcesByDateRelevancy<T extends ResourceForSorting>(
  resources: T[],
  startDate: Date | null,
  endDate?: Date | null
): T[] {
  if (resources.length === 0) return resources

  // If no start date provided (e.g. unscheduled moment), sort by most recently updated only
  if (!startDate) {
    return [...resources].sort((a, b) => compareDesc(a.updatedAt, b.updatedAt))
  }

  // If no end date provided, treat as single-day event
  const effectiveEndDate = endDate || startDate

  // Helper function to calculate date overlap in days
  const calculateOverlap = (planStart: Date, planEnd: Date): number => {
    const overlapStart = max([startDate, planStart])
    const overlapEnd = min([effectiveEndDate, planEnd])

    // If overlapStart is after overlapEnd, there's no overlap
    return overlapStart <= overlapEnd
      ? differenceInDays(overlapEnd, overlapStart) + 1
      : 0
  }

  return [...resources].sort((a, b) => {
    const overlapA = calculateOverlap(a.startAt, a.endAt)
    const overlapB = calculateOverlap(b.startAt, b.endAt)

    const planDurationA = differenceInDays(a.endAt, a.startAt) + 1
    const planDurationB = differenceInDays(b.endAt, b.startAt) + 1

    const relevancyA = overlapA > 0 ? overlapA / planDurationA : 0
    const relevancyB = overlapB > 0 ? overlapB / planDurationB : 0

    // First sort by relevancy percentage (higher = more relevant)
    if (relevancyA !== relevancyB) {
      return relevancyB - relevancyA
    }

    // Second sort by most recently updated (for same relevancy)
    return compareDesc(a.updatedAt, b.updatedAt)
  })
}
